import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { FilterItem } from '@/components/ui/custom/ComplexFilter/types';
import useLocalStorage from '@/hooks/useLocalStorage';
import { usePreferences } from '@/hooks/usePreferences';
import { Preferences } from '@/shared/types/user';
import { LENGTH_XS } from '@/util/validation/constants';

export interface SavedView {
  id: string;
  name: string;
  filters: FilterItem[];
}

interface UseTableFiltersOptions {
  storageKey: string;
  preferencesKey?: string;
}

const EMPTY_FILTERS: FilterItem[] = [];
const SAVED_FILTER_VIEWS_KEY = 'saved_filter_views';

/** Returns the values for a given filter key, or an empty array if not found. */
export const getFilterValues = (filters: FilterItem[], key: string): string[] => {
  return filters.find((f) => f.key === key)?.values ?? [];
};

/** Returns whether a filter is in exclude mode. */
export const isFilterExcluded = (filters: FilterItem[], key: string): boolean => {
  return filters.find((f) => f.key === key)?.exclude ?? false;
};

/**
 * Converts legacy flat-object filters to FilterItem[].
 *
 * Old: { types: ['support'], languages: ['en'], languageCondition: 'is not' }
 * New: [{ key: 'types', values: ['support'] }, { key: 'languages', values: ['en'], exclude: true }]
 */
export const migrateFilters = (raw: unknown): FilterItem[] => {
  // Assumes array items are valid FilterItem objects (no shape validation)
  if (Array.isArray(raw)) {
    return raw;
  }
  if (!raw || typeof raw !== 'object') {
    return [];
  }

  const obj = raw as Record<string, unknown>;

  return Object.entries(obj).flatMap(([key, value]) => {
    // Skip *Condition keys — languageCondition was the only one in the old format
    if (key.endsWith('Condition')) {
      return [];
    }

    let values: string[];
    if (Array.isArray(value)) {
      values = value;
    } else if (value != null) {
      values = [value as string];
    } else {
      values = [];
    }
    if (values.length === 0) {
      return [];
    }

    const item: FilterItem = { key, values };
    if (key === 'languages' && obj.languageCondition === 'is not') {
      item.exclude = true;
    }

    return [item];
  });
};

/** Preferences store saved views as `{ saved_filter_views: { [key]: { views: [...] } } }`. */
type SavedViewsMap = Record<string, { views: SavedView[] }>;

const readViews = (prefs: Preferences | undefined, key: string): SavedView[] => {
  const stored = prefs?.[SAVED_FILTER_VIEWS_KEY] as unknown as SavedViewsMap | undefined;
  const views = stored?.[key]?.views ?? [];
  return views.map((v) => ({ ...v, filters: migrateFilters(v.filters) }));
};

const buildPrefsWithViews = (
  prefs: Preferences | undefined,
  key: string,
  views: SavedView[]
): Preferences => {
  const updated = structuredClone(prefs ?? {});
  const stored = (updated[SAVED_FILTER_VIEWS_KEY] as unknown as SavedViewsMap) ?? {};
  stored[key] = { views };
  updated[SAVED_FILTER_VIEWS_KEY] = stored as unknown as Preferences[string];
  return updated;
};

/**
 * Manages table filter state (localStorage) and saved filter views (user preferences).
 */
export const useTableFilters = (options: UseTableFiltersOptions) => {
  const { storageKey, preferencesKey } = options;
  const { t } = useTranslation();
  const { accountUserPrefs, updateAccountUserPreferences } = usePreferences();

  // --- Filter state ---

  const [rawFilters, setFilters] = useLocalStorage<FilterItem[]>(storageKey, EMPTY_FILTERS);
  const filters = useMemo(() => migrateFilters(rawFilters), [rawFilters]);

  const activeFilterCount = filters.length;

  // --- Filter actions ---

  const setFilter = useCallback(
    (key: string, values: string[], exclude?: boolean) => {
      setFilters((prev) => {
        const withoutKey = prev.filter((f) => f.key !== key);

        if (values.length === 0) {
          return withoutKey;
        }

        const item: FilterItem = { key, values };
        if (exclude) {
          item.exclude = true;
        }
        return [...withoutKey, item];
      });
    },
    [setFilters]
  );

  const removeFilter = useCallback(
    (...keys: string[]) => {
      setFilters((prev) => prev.filter((f) => !keys.includes(f.key)));
    },
    [setFilters]
  );

  const resetFilters = useCallback(() => setFilters(EMPTY_FILTERS), [setFilters]);

  // --- View state ---

  const [activeViewId, setActiveViewId] = useLocalStorage<string | null>(
    `${storageKey}ActiveView`,
    null
  );

  const savedViews = useMemo(
    () => (preferencesKey ? readViews(accountUserPrefs, preferencesKey) : []),
    [accountUserPrefs, preferencesKey]
  );

  const activeView = useMemo(
    () => savedViews.find((v) => v.id === activeViewId) ?? null,
    [savedViews, activeViewId]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!activeView) {
      return false;
    }
    return JSON.stringify(filters) !== JSON.stringify(activeView.filters);
  }, [filters, activeView]);

  // --- View actions ---

  const persistViews = useCallback(
    (views: SavedView[], onSuccess?: () => void) => {
      if (!preferencesKey) {
        return;
      }
      updateAccountUserPreferences(buildPrefsWithViews(accountUserPrefs, preferencesKey, views), {
        onSuccess,
      });
    },
    [accountUserPrefs, preferencesKey, updateAccountUserPreferences]
  );

  const validateViewName = useCallback(
    (name: string, existingViews: SavedView[]): string | null => {
      const trimmed = name.trim();
      if (!trimmed) {
        return t('validation.required');
      }
      if (trimmed.length > LENGTH_XS) {
        return t('validation.max_length', { 0: LENGTH_XS });
      }
      if (existingViews.some((v) => v.name === trimmed)) {
        return t('validation.name_already_exists');
      }
      return null;
    },
    [t]
  );

  const applyView = useCallback(
    (view: SavedView) => {
      setActiveViewId(view.id);
      setFilters(view.filters);
    },
    [setActiveViewId, setFilters]
  );

  const clearActiveView = useCallback(() => {
    setActiveViewId(null);
  }, [setActiveViewId]);

  const saveView = useCallback(
    (name: string): string | null => {
      if (!preferencesKey) {
        return null;
      }
      const error = validateViewName(name, savedViews);
      if (error) {
        return error;
      }
      const trimmed = name.trim();
      const id = crypto.randomUUID();
      const newView: SavedView = { id, name: trimmed, filters };
      persistViews([...savedViews, newView], () => {
        toast.success(t('filters.views.saved', { 0: trimmed }));
      });
      setActiveViewId(id);
      return null;
    },
    [preferencesKey, filters, savedViews, persistViews, validateViewName, setActiveViewId, t]
  );

  const renameView = useCallback(
    (name: string): string | null => {
      if (!preferencesKey || !activeViewId) {
        return null;
      }
      const otherViews = savedViews.filter((v) => v.id !== activeViewId);
      const error = validateViewName(name, otherViews);
      if (error) {
        return error;
      }
      const trimmed = name.trim();
      const updated = savedViews.map((v) => (v.id === activeViewId ? { ...v, name: trimmed } : v));
      persistViews(updated, () => {
        toast.success(t('filters.views.updated', { 0: trimmed }));
      });
      return null;
    },
    [preferencesKey, activeViewId, savedViews, persistViews, validateViewName, t]
  );

  const updateView = useCallback(() => {
    if (!preferencesKey || !activeViewId) {
      return;
    }
    const updated = savedViews.map((v) => (v.id === activeViewId ? { ...v, filters } : v));
    persistViews(updated, () => {
      toast.success(t('filters.views.updated', { 0: activeView?.name }));
    });
  }, [preferencesKey, activeViewId, activeView, filters, savedViews, persistViews, t]);

  const deleteView = useCallback(() => {
    if (!preferencesKey || !activeViewId) {
      return;
    }
    const viewName = activeView?.name;
    const updated = savedViews.filter((v) => v.id !== activeViewId);
    persistViews(updated, () => {
      toast.success(t('filters.views.deleted', { 0: viewName }));
    });
    setActiveViewId(null);
    setFilters(EMPTY_FILTERS);
  }, [
    preferencesKey,
    activeViewId,
    activeView,
    savedViews,
    persistViews,
    setActiveViewId,
    setFilters,
    t,
  ]);

  return {
    filters,
    activeFilterCount,
    setFilter,
    removeFilter,
    resetFilters,
    savedViews,
    activeView,
    hasUnsavedChanges,
    applyView,
    clearActiveView,
    saveView,
    renameView,
    updateView,
    deleteView,
  };
};
